import { useCallback, useEffect, useRef, useState } from 'react';
import { getEligibilityChecks } from '../services/api';
import type { EligibilityCheckResponse } from '../services/api';
import type { PatientInsuranceResponse } from '../services/api/types.gen';
import { latestCheck } from '../components/eligibility/eligibilityStatus';
import { eligibilityBlockReason } from '../components/eligibility/eligibilityBlock';

/**
 * A patient's eligibility checks, already attributed to a coverage.
 *
 * The backend answers "which coverage does this check speak for" in
 * `effective_patient_insurance_id` (see `resolve_effective_coverage_ids`). This
 * hook renders that answer and deliberately does not re-derive it.
 */
export function usePatientEligibilityChecks(
  patientId: string | undefined,
  /** Skip fetching until the surface is actually visible. */
  enabled: boolean = true
) {
  const [checks, setChecks] = useState<EligibilityCheckResponse[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  // Bumped on every load so a response that resolves after a newer load (or
  // after a patient switch) cannot write its rows into the current chart.
  const seqRef = useRef(0);

  const reload = useCallback(async () => {
    if (!patientId || !enabled) return;
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const res = await getEligibilityChecks({
        query: { patient_id: patientId },
      });
      if (seq !== seqRef.current) return;
      // The generated client reports errors on the result rather than throwing,
      // so without this branch a failed load is indistinguishable from a patient
      // who has never been checked.
      if (res.error) {
        console.error('Failed to load eligibility checks:', res.error);
        setFailed(true);
        return;
      }
      setChecks(res.data ?? []);
      setFailed(false);
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error('Failed to load eligibility checks:', err);
      setFailed(true);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [patientId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const latestFor = useCallback(
    (insurance: PatientInsuranceResponse): EligibilityCheckResponse | null =>
      latestCheck(
        checks.filter(c => c.effective_patient_insurance_id === insurance.id)
      ),
    [checks]
  );

  /** Why this coverage cannot be checked, or undefined when it can. */
  const blockedReasonFor = useCallback(
    (insurance: PatientInsuranceResponse): string | undefined =>
      // Shared with the intake Eligibility tab, and worded the same as what
      // run_eligibility_check refuses with, so neither surface nor the server's
      // 400 can disagree about one coverage.
      eligibilityBlockReason({
        supported: Boolean(insurance.eligibility_supported),
        enrollmentStatus: insurance.eligibility_enrollment_status,
        hasMemberId: Boolean(insurance.policy_number),
      }),
    []
  );

  return {
    checks,
    latestFor,
    blockedReasonFor,
    reload,
    loading,
    failed,
    /**
     * The checks are not known yet. Surfaces that would otherwise draw "Not
     * verified" on first paint wait on this. True before the first load
     * settles, so the first committed frame is not mistaken for an answer.
     */
    resolving: loading,
  };
}
